// Mentorist Design System
// Complete theme configuration for consistent styling across the app

export const Colors = {
  // Primary Colors - Dark Teal from Mentorist logo (from style guide)
  primary: '#004759',          // primary-700 - Logo color, main brand
  primaryLight: 'rgba(0, 71, 89, 0.1)', // primary with opacity
  primaryDark: '#003642',      // primary-800
  primary50: '#E6F2F5',        // Very light teal
  primary100: '#CCE5EB',       // Light teal
  primary200: '#99CBD7',       // 
  primary300: '#66B1C3',       //
  primary400: '#3397AF',       //
  primary500: '#007D9B',       // Bright teal - CTA buttons
  primary600: '#00637C',       // Hover states
  primary700: '#004759',       // Logo color (DEFAULT)
  primary800: '#003642',       // Dark teal
  primary900: '#00242B',       // Very dark teal
  
  // Accent Colors - Coral/Orange (from style guide)
  accent: '#FF9F7A',           // accent-500 - Main accent
  accent50: '#FFF5F0',         // Very light coral
  accent100: '#FFEBE1',        // Light coral
  accent200: '#FFD7C3',        // Borders for action cards
  accent300: '#FFC3A5',        //
  accent400: '#FFAF87',        //
  accent500: '#FF9F7A',        // Main accent (DEFAULT)
  accent600: '#FF7F4F',        // Icons, hover states
  accent700: '#FF5F24',        // Vibrant orange
  accent800: '#F84900',        //
  accent900: '#C33A00',        // Deep orange
  
  // Warm Colors - Cream from logo (from style guide)
  warm: '#FFF1D5',             // warm-100 - Light cream
  warm50: '#FFFAF0',           // Very light cream
  warm100: '#FFF1D5',          // Logo color (DEFAULT)
  warm200: '#FFE8BB',          //
  warm300: '#FFDFA1',          //
  warm400: '#FFD687',          //
  warm500: '#FFCD6D',          //
  warm600: '#F5B645',          //
  warm700: '#E09E1F',          //
  warm800: '#B37D0F',          //
  warm900: '#805707',          //
  
  // Text Colors - Gray scale (from style guide)
  textPrimary: '#111827',      // gray-900 - Main headings
  textSecondary: '#374151',    // gray-700 - Body text, dark body
  textTertiary: '#4B5563',     // gray-600 - Secondary text
  textMuted: '#6B7280',        // gray-500 - Muted text
  textDisabled: '#9CA3AF',     // gray-400 - Placeholder text
  textInverse: '#ffffff',      // white text
  
  // Section Headers - Dark teal for section titles
  sectionHeader: '#004759',    // primary-700 - Section titles (same as primary)
  
  // Background Colors - Warm cream tones (matching app screenshots)
  background: '#FAF8F3',       // Warm cream - Page backgrounds (instead of gray-50)
  surface: '#FFFEF9',          // Warm white - cards/surfaces (instead of pure white)
  surfaceSecondary: '#F5F1E8', // Light warm beige - Light backgrounds (instead of gray-100)
  
  // Border Colors - Warm tones
  border: '#E8E4D9',           // Warm beige border - Main border colors
  borderLight: '#F0EDE3',      // Light warm border - Borders, dividers
  borderActive: '#007D9B',     // primary-500 - Active/focused borders
  
  // Gray Scale (Tailwind grays from style guide)
  gray50: '#F9FAFB',           // Very light gray (backgrounds)
  gray100: '#F3F4F6',          // Light gray (backgrounds)
  gray200: '#E5E7EB',          // Border colors
  gray300: '#D1D5DB',          // Borders, dividers
  gray400: '#9CA3AF',          // Placeholder text
  gray500: '#6B7280',          // Secondary text
  gray600: '#4B5563',          // Body text
  gray700: '#374151',          // Dark body text
  gray800: '#1F2937',          // Headings
  gray900: '#111827',          // Very dark (headers, strong emphasis)
  
  // Status Colors
  success: '#10b981',          // green-500
  warning: '#f59e0b',          // amber-500
  error: '#ef4444',            // red-500
  
  // Interactive States
  overlay: 'rgba(255, 255, 255, 0.9)',
  disabled: '#E5E7EB',
  hover: '#F3F4F6',
  
  // Special
  transparent: 'transparent',
};

export const Typography = {
  // Font Families
  fontFamily: {
    regular: 'Poppins-Regular',
    medium: 'Poppins-Medium',
    semiBold: 'Poppins-SemiBold',
    bold: 'Poppins-Bold',
    light: 'Poppins-Regular',  // Using regular as fallback for light
  },
  
  // Font Sizes & Line Heights
  // Adapted from web style guide for mobile app (slightly smaller for mobile screens)
  heading1: {
    fontSize: 32,              // H1 - Hero titles (web: 36-48px, mobile optimized)
    lineHeight: 40,
    fontFamily: 'Poppins-Bold',
    fontWeight: '700' as const,
  },
  heading2: {
    fontSize: 28,              // H2 - Section titles (web: 30-36px, mobile optimized)
    lineHeight: 36,
    fontFamily: 'Poppins-Bold',
    fontWeight: '700' as const,
  },
  heading3: {
    fontSize: 24,              // H3 - Subsection titles (web: 24-30px)
    lineHeight: 32,
    fontFamily: 'Poppins-Bold',
    fontWeight: '700' as const,
  },
  heading4: {
    fontSize: 20,              // H4 - Card titles (web: 20px)
    lineHeight: 28,
    fontFamily: 'Poppins-Bold',
    fontWeight: '700' as const,
  },
  body1: {
    fontSize: 16,              // Regular body (web: 16px)
    lineHeight: 24,
    fontFamily: 'Poppins-Regular',
  },
  body2: {
    fontSize: 14,              // Small text (web: 14px)
    lineHeight: 20,
    fontFamily: 'Poppins-Regular',
  },
  caption: {
    fontSize: 12,              // Extra small (web: 12px)
    lineHeight: 16,
    fontFamily: 'Poppins-Regular',
  },
  button: {
    fontSize: 16,              // Button text
    lineHeight: 24,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600' as const,
  },
};

export const Spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
  xxxxl: 40,
};

export const BorderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  round: 50,
  circle: 999,
};

export const Shadows = {
  none: {
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 5,
  },
  bookCover: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
  },
};

// Component Style Definitions
export const ComponentStyles = {
  // Container Patterns
  pageContainer: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  contentContainer: {
    paddingHorizontal: Spacing.xxl,
    paddingVertical: Spacing.xxxl,
  },
  headerContainer: {
    gap: Spacing.sm,
    alignSelf: 'stretch' as const,
    marginBottom: Spacing.xxl,
  },
  
  // Card Patterns
  card: {
    backgroundColor: Colors.surface,
    borderRadius: BorderRadius.xl,
    padding: Spacing.xxl,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  cardSelected: {
    backgroundColor: Colors.primaryLight,
    borderColor: Colors.borderActive,
  },
  
  // Button Patterns
  buttonPrimary: {
    borderRadius: BorderRadius.round,
    paddingHorizontal: Spacing.xxxl,
    paddingVertical: 13,
    backgroundColor: Colors.primary,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'stretch' as const,
  },
  buttonDisabled: {
    backgroundColor: Colors.disabled,
  },
  
  // Input Patterns
  inputContainer: {
    borderRadius: BorderRadius.md,
    backgroundColor: Colors.surfaceSecondary,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  inputFocused: {
    borderColor: Colors.borderActive,
    backgroundColor: Colors.surface,
  },
};

// Text Style Presets
export const TextStyles = {
  title: {
    ...Typography.heading2,
    color: Colors.textPrimary,
    textAlign: 'center' as const,
  },
  subtitle: {
    ...Typography.heading3,
    color: Colors.textSecondary,
    textAlign: 'center' as const,
  },
  body: {
    ...Typography.body1,
    color: Colors.textPrimary,
  },
  bodySecondary: {
    ...Typography.body2,
    color: Colors.textSecondary,
  },
  caption: {
    ...Typography.caption,
    color: Colors.textSecondary,
  },
  buttonPrimary: {
    ...Typography.button,
    color: Colors.textInverse,
    textAlign: 'center' as const,
  },
  buttonSecondary: {
    ...Typography.button,
    color: Colors.primary,
    textAlign: 'center' as const,
  },
};

// Animation Durations
export const Animation = {
  fast: 200,
  normal: 300,
  slow: 500,
};

// Layout Constants
export const Layout = {
  headerHeight: 60,
  bottomSafeArea: 20,
  minTouchTarget: 44,
};

// Export all for convenience
export const Theme = {
  colors: Colors,
  typography: Typography,
  spacing: Spacing,
  borderRadius: BorderRadius,
  shadows: Shadows,
  components: ComponentStyles,
  textStyles: TextStyles,
  animation: Animation,
  layout: Layout,
};

export default Theme; 